import { Component } from 'react';

// T099 — Render-error boundary. Before this, a single throw during render
// (one malformed recommendation, one null deref in a table row) unmounted
// the ENTIRE app to a white screen — including mid-scan. React only
// catches render errors in class components, hence the class.
//
// Used at two levels (App.jsx):
//   - top-level, around <Routes> — catches Layout/auth/palette failures
//   - per-page, inside <Layout> — a crash in one page keeps the sidebar
//     alive so the operator can navigate away instead of reloading.
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    // `throw undefined/null` would otherwise store a falsy error, render
    // the children again, and re-throw in a loop. Normalise to an Error.
    return { error: error ?? new Error('Unknown render error (falsy throw)') };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary:${this.props.section ?? 'app'}]`, error, info?.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const section = this.props.section ?? 'this section';
    return (
      <div role="alert" className="m-4 rounded-xl border border-red-900/60 bg-red-950/20 p-6">
        <p className="text-[10px] uppercase tracking-wide text-red-400">Something broke</p>
        <h2 className="mt-1 text-base font-semibold text-slate-100">
          {section} hit a rendering error
        </h2>
        <p className="mt-2 max-w-xl text-sm text-slate-400">
          The rest of the app is still running. You can retry this view, or reload the page if it
          keeps happening.
        </p>
        <pre className="mt-3 max-h-32 overflow-auto rounded-md bg-slate-950/60 px-3 py-2 text-xs text-red-300">
          {String(error?.message ?? error)}
        </pre>
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={this.reset}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-100"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
